import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { togglePillClass } from '@course/components/toggle-pill';
import { MAX_BETS } from 'shared/tracker-config';
import {
  ADD_ANOTHER_BET_MESSAGE,
  betLabel,
  betPeopleText,
  MAX_BET_NAME_LENGTH,
  MAX_BET_OFFER_LENGTH,
  TrackerBet,
  TrackerBetGroup,
} from 'shared/tracker-people';
import { FieldError } from './panel';
import {
  CARD_CLASS,
  HELP_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  LINK_CLASS,
  Person,
  readError,
  readLocal,
  writeLocal,
} from './helpers';

export const BETS_HELP_SHORT = 'One offer aimed at one audience.';
export const BETS_HELP = `${BETS_HELP_SHORT} Every person you add goes under one bet.`;

function lastBetKey(courseId: string) {
  return `tracker-last-bet:${courseId}`;
}

/** The bet a form starts on: the only one, else the last one used in this browser. */
export function defaultBetId(bets: TrackerBet[], courseId: string) {
  if (bets.length === 1) {
    return bets[0].id;
  }
  const saved = readLocal(lastBetKey(courseId));
  return bets.find((b) => b.id === saved)?.id;
}

export function rememberBet(courseId: string, betId?: string) {
  if (betId) {
    writeLocal(lastBetKey(courseId), betId);
  }
}

/** A required Bet select, each option "Coaches · 90-day coaching package". */
export function BetSelect({
  bets,
  value,
  onChange,
  label = 'Bet',
  error,
  optional,
}: {
  bets: TrackerBet[];
  value?: string;
  onChange: (betId: string) => void;
  label?: string;
  error?: string;
  /** A person added before bets were on: no bet until one is picked. */
  optional?: boolean;
}) {
  return (
    <label class="flex flex-col gap-1">
      <span class={LABEL_CLASS}>{label}</span>
      <select
        class={`${INPUT_CLASS} ${error ? 'border-red-500' : ''}`}
        required={!optional}
        value={value ?? ''}
        aria-describedby={error ? 'bet-select-error' : undefined}
        onChange={(e) => onChange(e.currentTarget.value)}
      >
        {!value && (
          <option value="" disabled>
            {optional ? 'No bet yet' : 'Pick a bet'}
          </option>
        )}
        {bets.map((b) => (
          <option key={b.id} value={b.id}>
            {betLabel(b)}
          </option>
        ))}
      </select>
      {error && (
        <p id="bet-select-error" role="alert" class="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </label>
  );
}

type BetRow = { key: string; id?: string; name: string; offer: string };

/** People moved off a removed bet onto another, so the page can follow. */
export type BetMove = {
  from: string;
  to: string;
  /** For the toast: "Moved 12 people to Coaches. Removed Consultants." */
  fromName: string;
  toName: string;
  people: number;
};

export type SavedBets = { bets: TrackerBet[]; moved: BetMove[] };

let rowSeq = 0;

function rowsOf(bets: TrackerBet[]): BetRow[] {
  const rows = bets.map((b) => ({
    key: b.id,
    id: b.id,
    name: b.name,
    offer: b.offer,
  }));
  return rows.length ? rows : [{ key: `new-${rowSeq++}`, name: '', offer: '' }];
}

/**
 * The bets a participant is editing: rows of Name and Offer in order, saved
 * through the bet endpoints. Removing a bet with people asks which bet they
 * move to. Removals go first, so a full list can swap a bet.
 */
export function useBetsDraft(bets: TrackerBet[], people: Person[]) {
  const [rows, setRows] = useState(() => rowsOf(bets));
  const [removed, setRemoved] = useState<BetMove[]>([]);
  const [removedEmpty, setRemovedEmpty] = useState<string[]>([]);
  const [confirming, setConfirming] = useState<{ key: string; moveTo?: string } | undefined>();
  const [error, setError] = useState<string | undefined>();

  function peopleUnder(betId: string) {
    return people.filter((p) => p.betId === betId).length;
  }

  function patch(key: string, change: Partial<BetRow>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...change } : r)));
    setError(undefined);
  }

  function add() {
    setRows((rs) => [...rs, { key: `new-${rowSeq++}`, name: '', offer: '' }]);
  }

  function move(key: string, offset: number) {
    setRows((rs) => {
      const i = rs.findIndex((r) => r.key === key);
      const j = i + offset;
      if (i < 0 || j < 0 || j >= rs.length) {
        return rs;
      }
      const next = [...rs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  /** Saved bets that people can move to: every other one still in the list. */
  function moveTargets(row: BetRow) {
    return rows.filter((r) => r.id && r.key !== row.key);
  }

  function drop(row: BetRow) {
    setRows((rs) => rs.filter((r) => r.key !== row.key));
    setConfirming(undefined);
    setError(undefined);
  }

  function remove(row: BetRow) {
    if (row.id && peopleUnder(row.id) > 0) {
      setConfirming({ key: row.key, moveTo: moveTargets(row)[0]?.id });
      return;
    }
    if (row.id) {
      setRemovedEmpty((ids) => [...ids, row.id!]);
    }
    drop(row);
  }

  function confirmRemove(row: BetRow) {
    if (!row.id || !confirming?.moveTo) {
      return;
    }
    const to = confirming.moveTo;
    const toName = rows.find((r) => r.id === to)?.name.trim() ?? '';
    setRemoved((moves) => [
      ...moves.map((m) => (m.to === row.id ? { ...m, to, toName } : m)),
      {
        from: row.id!,
        to,
        fromName: row.name.trim(),
        toName,
        people: peopleUnder(row.id!),
      },
    ]);
    drop(row);
  }

  /** Why the rows can't be saved yet, or undefined. */
  function problem() {
    const kept = rows.filter((r) => r.id || r.name.trim() || r.offer.trim());
    if (!kept.length) {
      return 'Add at least one bet.';
    }
    if (kept.some((r) => !r.name.trim())) {
      return 'Name each bet, like “Coaches”.';
    }
  }

  /** Saves every change; returns the bets as the server has them, and who moved. */
  async function save(courseId: UUID): Promise<SavedBets> {
    let latest = bets;
    const moved: BetMove[] = [];
    const done = new Set<string>();
    const added = new Map<string, string>();
    try {
      for (const betId of removedEmpty) {
        latest = await rpx.tracker.removeBet({ courseId, betId: betId as UUID });
        done.add(betId);
      }
      for (const m of removed) {
        latest = await rpx.tracker.removeBet({
          courseId,
          betId: m.from as UUID,
          moveToBetId: m.to as UUID,
        });
        done.add(m.from);
        moved.push(m);
        showToast({
          type: 'ok',
          title: `Moved ${m.people === 1 ? '1 person' : `${m.people} people`} to ${m.toName}. Removed ${m.fromName}.`,
          message: '',
        });
      }
      for (const row of rows) {
        const bet = { name: row.name.trim(), offer: row.offer.trim() };
        if (row.id) {
          const was = bets.find((b) => b.id === row.id);
          if (was && (was.name !== bet.name || was.offer !== bet.offer)) {
            latest = await rpx.tracker.updateBet({ courseId, betId: row.id as UUID, bet });
          }
        } else if (bet.name || bet.offer) {
          const before = new Set(latest.map((b) => b.id));
          latest = await rpx.tracker.addBet({ courseId, bet });
          const fresh = latest.find((b) => !before.has(b.id));
          if (fresh) {
            added.set(row.key, fresh.id);
          }
        }
      }
      const order = rows.map((r) => r.id ?? added.get(r.key)).filter((id): id is string => !!id);
      if (order.join() !== latest.map((b) => b.id).join() && order.length) {
        latest = await rpx.tracker.reorderBets({ courseId, betIds: order as UUID[] });
      }
      setRemoved([]);
      setRemovedEmpty([]);
      setRows(rowsOf(latest));
      return { bets: latest, moved };
    } catch (err) {
      setRemoved((moves) => moves.filter((m) => !done.has(m.from)));
      setRemovedEmpty((ids) => ids.filter((id) => !done.has(id)));
      setRows((rs) =>
        rs.map((r) =>
          added.has(r.key) ? { ...r, id: added.get(r.key), key: added.get(r.key)! } : r,
        ),
      );
      setError(readError(err).message);
      const saved: SavedBets = { bets: latest, moved };
      throw Object.assign(new Error(readError(err).message), { saved });
    }
  }

  return {
    rows,
    error,
    setError,
    patch,
    add,
    move,
    remove,
    confirming,
    setConfirming,
    confirmRemove,
    moveTargets,
    peopleUnder,
    problem,
    save,
  };
}

/** What a failed save did manage to write, if anything. */
export function partialSave(err: unknown) {
  return (err as { saved?: SavedBets }).saved;
}

export type BetsDraft = ReturnType<typeof useBetsDraft>;

function betFilterKey(courseId: string) {
  return `tracker-bet-filter:${courseId}`;
}

/** The bet the switch last showed for this course, if it still exists. */
export function savedBetFilter(bets: TrackerBetGroup[], courseId: string) {
  const saved = readLocal(betFilterKey(courseId));
  return bets.find((b) => b.id === saved)?.id;
}

export function saveBetFilter(courseId: string, betId?: string) {
  writeLocal(betFilterKey(courseId), betId ?? '');
}

/**
 * "All bets · Coaches · Consultants": filters the totals strip and the list
 * to one bet's share. One row of pills in the filters' shared look, which
 * scrolls sideways on a phone; names cut at about 14 characters there.
 */
export function BetSwitch({
  bets,
  value,
  onChange,
}: {
  bets: TrackerBetGroup[];
  value?: string;
  onChange: (betId?: string) => void;
}) {
  const options: Array<{ id?: string; label: string }> = [
    { label: 'All bets' },
    ...bets.map((b) => ({ id: b.id, label: b.name })),
  ];
  return (
    <div class="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
      <div class="flex w-max gap-2" role="group" aria-label="Bet">
        {options.map((o) => (
          <button
            key={o.id ?? 'all'}
            type="button"
            title={o.label}
            aria-pressed={value === o.id}
            class={`shrink-0 whitespace-nowrap ${togglePillClass(value === o.id)}`}
            onClick={() => onChange(o.id)}
          >
            <span class="block max-w-[14ch] truncate sm:max-w-none">{o.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * "Your bets" as its own small card, on a course with bets on but no revenue
 * scoreboard to set them up beside. The form until there is a bet; then the
 * list, with Change.
 */
export function BetsCard({
  courseId,
  bets,
  people,
  onBets,
}: {
  courseId: UUID;
  bets: TrackerBet[];
  people: Person[];
  onBets: (saved: SavedBets) => void;
}) {
  const [changing, setChanging] = useState(false);
  const editing = changing || !bets.length;
  return (
    <section class={CARD_CLASS} aria-label="Your bets">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">Your bets</h2>
        {!editing && (
          <Button class={LINK_CLASS} onClick={() => setChanging(true)}>
            Change
          </Button>
        )}
      </div>
      {editing ? (
        <BetsForm
          courseId={courseId}
          bets={bets}
          people={people}
          onSaved={(saved) => {
            onBets(saved);
            setChanging(false);
          }}
          onChanged={onBets}
          onCancel={bets.length ? () => setChanging(false) : undefined}
        />
      ) : (
        <ul class="flex flex-col gap-1 text-sm">
          {bets.map((b) => (
            <li key={b.id} class="text-gray-800 dark:text-gray-200">
              {betLabel(b)}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const ARROW_CLASS =
  'min-h-11 min-w-11 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30';

/**
 * Removing a bet with people: "12 people are under “Coaches”. Move them to
 * [bet] and remove." With no other bet to move them to, the way out instead.
 */
function RemoveConfirm({ draft, row }: { draft: BetsDraft; row: BetRow }) {
  const targets = draft.moveTargets(row);
  const name = row.name.trim() || 'this bet';
  const lead = betPeopleText(draft.peopleUnder(row.id!), name);
  return (
    <div
      role="alert"
      class="flex flex-col gap-2 rounded-md border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 p-3 text-sm text-gray-900 dark:text-gray-100"
    >
      {targets.length ? (
        <>
          <span class="flex flex-wrap items-center gap-2">
            {lead} Move them to
            <select
              class={`${INPUT_CLASS} w-auto max-w-full`}
              aria-label="Bet to move them to"
              value={draft.confirming?.moveTo}
              onChange={(e) => draft.setConfirming({ key: row.key, moveTo: e.currentTarget.value })}
            >
              {targets.map((t) => (
                <option key={t.key} value={t.id}>
                  {t.name.trim() || t.offer.trim()}
                </option>
              ))}
            </select>
            and remove “{name}”.
          </span>
          <span class="flex gap-2">
            <BtnPrimary type="button" onClick={() => draft.confirmRemove(row)}>
              Move and remove
            </BtnPrimary>
            <BtnSecondary type="button" onClick={() => draft.setConfirming(undefined)}>
              Keep bet
            </BtnSecondary>
          </span>
        </>
      ) : (
        <>
          <span>
            {lead} {ADD_ANOTHER_BET_MESSAGE}
          </span>
          <BtnSecondary
            type="button"
            class="self-start"
            onClick={() => draft.setConfirming(undefined)}
          >
            OK
          </BtnSecondary>
        </>
      )}
    </div>
  );
}

/** "Your bets": the rows in order, Add bet up to MAX_BETS, and Remove on each. */
export function BetsFields({
  draft,
  heading = true,
  help = BETS_HELP,
}: {
  draft: BetsDraft;
  heading?: boolean;
  /** Shorter where a header already says every person goes under one bet. */
  help?: string;
}) {
  return (
    <fieldset class="flex flex-col gap-3">
      {heading && <legend class={`${LABEL_CLASS} mb-1`}>Your bets</legend>}
      <p class={HELP_CLASS}>{help}</p>
      {draft.rows.map((row, i) => (
        <div key={row.key} class="flex flex-col gap-2">
          <div class="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] gap-2 sm:items-end">
            <label class="flex flex-col gap-1">
              <span class="text-xs text-gray-600 dark:text-gray-400">Name</span>
              <input
                class={INPUT_CLASS}
                placeholder={i === 0 ? 'e.g. Coaches' : ''}
                aria-label={`Bet ${i + 1} name`}
                maxLength={MAX_BET_NAME_LENGTH}
                value={row.name}
                onInput={(e) => draft.patch(row.key, { name: e.currentTarget.value })}
              />
            </label>
            <label class="flex flex-col gap-1">
              <span class="text-xs text-gray-600 dark:text-gray-400">Offer</span>
              <input
                class={INPUT_CLASS}
                placeholder={i === 0 ? 'e.g. 90-day coaching package' : ''}
                aria-label={`Bet ${i + 1} offer`}
                maxLength={MAX_BET_OFFER_LENGTH}
                value={row.offer}
                onInput={(e) => draft.patch(row.key, { offer: e.currentTarget.value })}
              />
            </label>
            <span class="flex items-center gap-1 self-start sm:self-end">
              {draft.rows.length > 1 && (
                <>
                  <Button
                    class={ARROW_CLASS}
                    aria-label={`Move bet ${i + 1} up`}
                    disabled={i === 0}
                    onClick={() => draft.move(row.key, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    class={ARROW_CLASS}
                    aria-label={`Move bet ${i + 1} down`}
                    disabled={i === draft.rows.length - 1}
                    onClick={() => draft.move(row.key, 1)}
                  >
                    ↓
                  </Button>
                </>
              )}
              {(row.id || draft.rows.length > 1) && (
                <Button
                  class={`${LINK_CLASS} min-h-11 px-1`}
                  aria-label={`Remove bet ${i + 1}`}
                  onClick={() => draft.remove(row)}
                >
                  Remove
                </Button>
              )}
            </span>
          </div>
          {draft.confirming?.key === row.key && row.id && <RemoveConfirm draft={draft} row={row} />}
        </div>
      ))}
      {draft.rows.length < MAX_BETS && (
        <Button class={`${LINK_CLASS} self-start`} onClick={draft.add}>
          Add bet
        </Button>
      )}
      <FieldError id="bets-error" message={draft.error} />
    </fieldset>
  );
}

/**
 * The bets form on its own: the small "Your bets" card on a course without
 * a revenue scoreboard, and the "Add your bets first" panel.
 */
export function BetsForm({
  courseId,
  bets,
  people,
  onSaved,
  onChanged,
  onCancel,
  saveLabel = 'Save bets',
  help,
}: {
  courseId: UUID;
  bets: TrackerBet[];
  people: Person[];
  onSaved: (saved: SavedBets) => void;
  /** Part of a save went through before an error: the bets as they now are. */
  onChanged: (saved: SavedBets) => void;
  onCancel?: () => void;
  saveLabel?: string;
  help?: string;
}) {
  const draft = useBetsDraft(bets, people);
  const [saving, setSaving] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    const problem = draft.problem();
    if (problem) {
      draft.setError(problem);
      return;
    }
    setSaving(true);
    try {
      onSaved(await draft.save(courseId));
    } catch (err) {
      const saved = partialSave(err);
      if (saved) {
        onChanged(saved);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="flex flex-col gap-3" onSubmit={save} aria-label="Your bets">
      <BetsFields draft={draft} heading={false} help={help} />
      <div class="flex gap-2">
        <BtnPrimary type="submit" isLoading={saving}>
          {saveLabel}
        </BtnPrimary>
        {onCancel && (
          <BtnSecondary type="button" onClick={onCancel}>
            Cancel
          </BtnSecondary>
        )}
      </div>
    </form>
  );
}
